export const ACTION_COUNTER_ID = 'counter';

export const isActionTrackingEvent = (eventOrType) => {
  if (!eventOrType) return false;
  const type = typeof eventOrType === 'string'
    ? eventOrType
    : eventOrType.eventType;
  return String(type || '').toUpperCase() === 'ACTION_TRACKING';
};

export const normalizeInput = (raw) => {
  switch (String(raw || '').trim().toUpperCase()) {
    case 'DROPDOWN':
    case 'SELECT':
      return 'DROPDOWN';
    case 'RADIO':
      return 'RADIO';
    case 'CHECKBOX':
    case 'CHECK':
      return 'CHECKBOX';
    default:
      return 'TEXTBOX';
  }
};

export const parseOptions = (raw) => {
  if (Array.isArray(raw)) {
    return raw.map((item) => String(item || '').trim()).filter(Boolean);
  }
  return String(raw || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

export const parseEntities = (configuration = {}) => {
  const raw = configuration.entities;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((item) => item && typeof item === 'object')
    .map((item) => ({
      id: String(item.id || ''),
      name: String(item.name || '').trim(),
      unit: String(item.unit || '').trim(),
      kind: String(item.kind || 'NUMBER').toUpperCase() === 'COUNT' ? 'COUNT' : 'NUMBER',
      input: normalizeInput(item.input || item.inputType),
      options: parseOptions(item.options),
    }))
    .filter((item) => item.name);
};

export const parseEntries = (configuration = {}) => {
  const raw = configuration.entries;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((item) => item && typeof item === 'object')
    .map((item) => ({
      id: String(item.id || ''),
      entityId: String(item.entityId || ''),
      value: Number(item.value) || 0,
      at: item.at ? new Date(item.at) : new Date(),
      label: String(item.label || item.itemName || '').trim(),
      userId: String(item.userId || item.createdBy || '').trim(),
      userName: String(item.userName || item.createdByName || '').trim(),
    }))
    .sort((a, b) => b.at - a.at);
};

export const actionTrackKind = (configuration = {}, entities = parseEntities(configuration)) => {
  const raw = String(configuration.trackKind || '').trim().toUpperCase();
  if (raw === 'COUNT' || raw === 'NUMBER') return raw;
  if (entities.length && entities.every((item) => item.kind === 'COUNT')) return 'COUNT';
  return 'NUMBER';
};

export const formatTrackValue = (n) => {
  const value = Number(n) || 0;
  if (value === Math.round(value)) return String(Math.round(value));
  return value.toFixed(2);
};

const bucketStart = (at, grain) => {
  const t = at instanceof Date ? at : new Date(at);
  const year = t.getFullYear();
  const month = t.getMonth();
  const day = t.getDate();
  const hour = t.getHours();
  const minute = t.getMinutes();
  if (grain === 'minute') return new Date(year, month, day, hour, minute).getTime();
  if (grain === 'hour') return new Date(year, month, day, hour).getTime();
  if (grain === 'month') return new Date(year, month, 1).getTime();
  if (grain === 'year') return new Date(year, 0, 1).getTime();
  return new Date(year, month, day).getTime();
};

const timeline = (grain, now = new Date()) => {
  const origin = bucketStart(now, grain);
  const start = new Date(origin);
  const slots = [];
  const count = grain === 'year' ? 5 : grain === 'day' ? 14 : 12;
  for (let i = count - 1; i >= 0; i -= 1) {
    const next = new Date(start);
    if (grain === 'minute') next.setMinutes(start.getMinutes() - i);
    else if (grain === 'hour') next.setHours(start.getHours() - i);
    else if (grain === 'month') next.setMonth(start.getMonth() - i);
    else if (grain === 'year') next.setFullYear(start.getFullYear() - i);
    else next.setDate(start.getDate() - i);
    slots.push(next);
  }
  return slots;
};

const axisLabel = (at, grain) => {
  const t = at instanceof Date ? at : new Date(at);
  const pad = (n) => String(n).padStart(2, '0');
  if (grain === 'minute' || grain === 'hour') return `${pad(t.getHours())}:${pad(t.getMinutes())}`;
  if (grain === 'month') return `${t.getMonth() + 1}/${String(t.getFullYear()).slice(2)}`;
  if (grain === 'year') return String(t.getFullYear());
  return `${t.getDate()}/${t.getMonth() + 1}`;
};

export const actionChartBuckets = (entries, entityId, grain = 'day') => {
  const matching = (entries || []).filter((item) => String(item.entityId) === String(entityId));
  const sums = new Map();
  matching.forEach((item) => {
    const key = bucketStart(item.at, grain);
    sums.set(key, (sums.get(key) || 0) + (Number(item.value) || 0));
  });
  return timeline(grain).map((slot) => ({
    at: slot,
    value: sums.get(slot.getTime()) || 0,
    label: axisLabel(slot, grain),
  }));
};

export const entityNameFor = (entities, entityId, fallback = 'Count') => {
  const match = (entities || []).find((item) => item.id === entityId);
  return match?.name || fallback;
};
